import { Dispatch } from 'react';

import { AnyAction } from 'redux';

import { TASKTYPE } from 'common/constants';
import {
	PROGRESS_BAR_ELEMENTS,
	TASK_LIST_WORKFLOW_STATUS,
} from 'common/constants/CitationDetails';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { ACTION_PLAN_TAB_CONSTANTS } from 'common/constants/TaxonomyConstants';
import { appPages } from 'common/constants/TextContent';
import {
	setMappingDrawer,
	setRequirementInventoryBreadcrumbText,
	setSelectedEntityTypeData,
} from 'common/store/reducer/commonReducer';
import {
	setSelectedTabValue,
	setSelectedTask,
	setTabIndex,
} from 'common/store/reducer/mappingDetailsReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

const { requirementInventory } = TASK_LIST_WORKFLOW_STATUS;
const { TAB_TASKS, TAB_MAPPING } = ACTION_PLAN_TAB_CONSTANTS;
const {
	SUMMARY_REVIEW,
	SUMMARY_ASSESSMENT,
	MAPPING_REVIEW,
	MAPPING_ASSESSMENT,
	APPLICABILITY_REVIEW,
	APPLICABILITY_ASSESSMENT,
	GROUPING_ASSESSMENT,
	GROUPING_REVIEW,
	NOTSTARTED,
	COMPLETE,
} = PROGRESS_BAR_ELEMENTS;
const { WORKFLOW } = TASKTYPE;

const { HOME_PAGE } = appPages;
const {
	regulationInventory,
	overView,
	regulationMappingAssessment,
	regulationSummaryAssessment,
	regulatoryGroupAssessment,
	regulatoryApplicability,
	regulatoryGroupReview,
	horizonScanning,
	horizonScanningDetail,
	prcInventory,
} = rrcpDashboardRoutes;

/**
 * @method to handle accordion change
 */
export const handleAccordianChange = (
	event: any,
	res: string,
	selectedAccordians: string[],
	setSelectedAccordians: (value: React.SetStateAction<string[]>) => void,
) => {
	event.stopPropagation();
	if (selectedAccordians.includes(res)) {
		setSelectedAccordians((val) =>
			val.filter((accordian) => accordian !== res),
		);
	} else {
		setSelectedAccordians([...selectedAccordians, res]);
	}
};

/**
 * @method to set the requirement inventory details data
 */
export const handleRequirementNavigation = (
	params: any,
	setReqInvData: Dispatch<AnyAction>,
) => {
	const {
		assignedObjectType,
		assignedObjectId,
		assignedObjectName,
		relatedParentObjectId,
		relatedParentObjectType,
		id,
		status,
	} = params;
	let path = '';
	if (assignedObjectType !== requirementInventory) {
		setReqInvData(setMappingDrawer(true));
		setReqInvData(setTabIndex(TAB_TASKS));
		setReqInvData(
			setSelectedEntityTypeData({
				id: assignedObjectId,
				title: assignedObjectName,
				type: assignedObjectType.toLowerCase(),
			}),
		);
		setReqInvData(setSelectedTask(id));
		path = urlWithTenant(
			`/${regulationInventory}/${overView}/${regulationMappingAssessment}/${relatedParentObjectId}`,
		);
		return path;
	}

	const reqInvStatus = status ? status.toLowerCase() : '';

	switch (reqInvStatus) {
		case SUMMARY_REVIEW.toLowerCase():
		case SUMMARY_ASSESSMENT.toLowerCase():
			path = relatedParentObjectId
				? urlWithTenant(
						`/${regulationInventory}/${overView}/${regulationSummaryAssessment}/${assignedObjectId}`,
				  )
				: urlWithTenant(
						`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${assignedObjectId}`,
				  );
			break;
		case MAPPING_REVIEW.toLowerCase():
		case MAPPING_ASSESSMENT.toLowerCase():
			path = relatedParentObjectId
				? urlWithTenant(
						`/${regulationInventory}/${overView}/${regulationMappingAssessment}/${
							relatedParentObjectType === 'Group'
								? relatedParentObjectId
								: assignedObjectId
						}`,
				  )
				: urlWithTenant(
						`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${assignedObjectId}`,
				  );
			break;
		case APPLICABILITY_REVIEW.toLowerCase():
		case APPLICABILITY_ASSESSMENT.toLowerCase():
		case NOTSTARTED.toLowerCase():
			path = urlWithTenant(
				`/${regulationInventory}/${overView}/${regulatoryApplicability}/${assignedObjectId}`,
			);
			break;
		case GROUPING_ASSESSMENT.toLowerCase():
			path = urlWithTenant(
				`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${assignedObjectId}`,
			);
			break;
		case GROUPING_REVIEW.toLowerCase():
			path = relatedParentObjectId
				? urlWithTenant(
						`/${regulationInventory}/${overView}/${regulatoryGroupReview}/${assignedObjectId}`,
				  )
				: urlWithTenant(
						`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${assignedObjectId}`,
				  );
			break;
		case COMPLETE.toLowerCase():
			path = relatedParentObjectId
				? urlWithTenant(
						`/${regulationInventory}/${overView}/${regulationMappingAssessment}/${
							relatedParentObjectType === 'Group'
								? relatedParentObjectId
								: assignedObjectId
						}`,
				  )
				: urlWithTenant(
						`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${assignedObjectId}`,
				  );
			break;
		default:
			break;
	}
	return path;
};

/**
 * @method to navigate to requirements inventory module
 */
export const navigateToReqInventory = (
	params: any,
	setReqInvData: Dispatch<AnyAction>,
	isReqInvEnabled: boolean,
	toPath: any,
) => {
	setReqInvData(setRequirementInventoryBreadcrumbText(HOME_PAGE));
	const path = handleRequirementNavigation(params, setReqInvData);
	if (isReqInvEnabled) {
		toPath(path, {
			state: {
				isMappingDrawerOpen: params.assignedObjectType !== requirementInventory,
			},
		});
	}
};

/**
 * @method to navigate to horizon change module
 */
export const navigateToHorizonChange = (
	params: any,
	setStatus: Dispatch<AnyAction>,
	toPath: any,
) => {
	toPath(
		urlWithTenant(
			`/${horizonScanning}/${horizonScanningDetail}/${
				params.taskType === WORKFLOW
					? params?.assignedObjectId
					: params?.relatedParentObjectId
			}`,
		),
		{
			state: { routePath: HOME_PAGE },
		},
	);
	if (params.taskType === WORKFLOW) {
		setStatus(setSelectedTabValue(TAB_MAPPING));
	} else {
		// navigate to details page with side drawer open
		setStatus(setMappingDrawer(true));
		setStatus(setTabIndex(TAB_TASKS));
		setStatus(setSelectedTask(params.id));
		setStatus(setSelectedTabValue(TAB_TASKS));
	}
};

/**
 * @method to navigate to risk reference prcInventory module
 */
export const navigateToRiskRefTaxonomy = (
	params: any,
	setStatus: Dispatch<AnyAction>,
	toPath: any,
) => {
	const {
		assignedObjectId,
		propertyName,
		relatedParentObjectId,
		relatedParentObjectType,
	} = params;

	toPath(
		urlWithTenant(
			`/${prcInventory}/${relatedParentObjectId}/${relatedParentObjectType}/${assignedObjectId}/${propertyName}`,
		),
		{
			state: {
				routePath: HOME_PAGE,
			},
		},
	);
};
